import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExitIntentModal({ open, onClose }) {
    const [form, setForm] = useState({ name: "", email: "" });
    const [accepted, setAccepted] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Completa nombre y email para descargar la guía");
            return;
        }
        if (!accepted) {
            toast.error("Debes aceptar la Política de Privacidad para continuar");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/lead-magnet`, {
                name: form.name.trim(),
                email: form.email.trim(),
            });
            toast.success("Guía descargándose. Revisa también tu email.");
            // trigger download
            const a = document.createElement("a");
            a.href = data.pdf_url;
            a.download = "desactiva-7-senales.pdf";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setForm({ name: "", email: "" });
            setAccepted(false);
            onClose();
        } catch (err) {
            console.error(err);
            toast.error("No hemos podido procesar tu descarga. Inténtalo de nuevo.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent
                data-testid="exit-intent-modal"
                className="bg-ink-900 border border-gold/30 rounded-none max-w-xl p-0 overflow-hidden"
            >
                <div className="absolute -top-32 -left-24 blue-halo opacity-60" />
                <div className="relative p-8 sm:p-10">
                    <DialogHeader className="space-y-3 mb-6">
                        <p className="text-[10px] tracking-[0.3em] uppercase text-gold/80">
                            Espera. Antes de irte.
                        </p>
                        <DialogTitle className="font-serif text-3xl sm:text-4xl font-light text-white leading-tight">
                            Llévate las{" "}
                            <span className="text-gold italic">7 señales</span> de que
                            vives en piloto automático.
                        </DialogTitle>
                        <DialogDescription className="text-zinc-400 text-sm font-light leading-relaxed">
                            Una guía breve (PDF, 10 páginas) para identificar el patrón
                            que sigue dirigiendo tus decisiones — antes incluso de
                            empezar la mentoría.
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="space-y-2">
                            <Label
                                htmlFor="exit-name"
                                className="text-xs tracking-[0.18em] uppercase text-zinc-400"
                            >
                                Nombre
                            </Label>
                            <Input
                                id="exit-name"
                                data-testid="exit-name-input"
                                value={form.name}
                                onChange={(e) =>
                                    setForm({ ...form, name: e.target.value })
                                }
                                placeholder="Tu nombre"
                                className="bg-black/50 border-white/10 rounded-none h-12 focus:border-gold focus:ring-0"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label
                                htmlFor="exit-email"
                                className="text-xs tracking-[0.18em] uppercase text-zinc-400"
                            >
                                Email
                            </Label>
                            <Input
                                id="exit-email"
                                data-testid="exit-email-input"
                                type="email"
                                value={form.email}
                                onChange={(e) =>
                                    setForm({ ...form, email: e.target.value })
                                }
                                placeholder="tu@email.com"
                                className="bg-black/50 border-white/10 rounded-none h-12 focus:border-gold focus:ring-0"
                            />
                        </div>

                        <label className="flex items-start gap-3 text-xs text-zinc-400 font-light leading-relaxed cursor-pointer">
                            <input
                                type="checkbox"
                                checked={accepted}
                                onChange={(e) => setAccepted(e.target.checked)}
                                data-testid="exit-privacy-checkbox"
                                required
                                className="mt-0.5 accent-[#c7aa78] flex-shrink-0"
                            />
                            <span>
                                He leído y acepto la{" "}
                                <Link
                                    to="/politica-de-privacidad"
                                    target="_blank"
                                    data-testid="exit-privacy-link"
                                    className="text-gold underline underline-offset-2 hover:text-beam"
                                >
                                    Política de Privacidad
                                </Link>
                                .
                            </span>
                        </label>

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="exit-submit-button"
                            className="btn-gold w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? "Enviando..." : "Descargar la guía"}
                        </button>

                        <p
                            data-testid="exit-privacy-info"
                            className="text-[10px] text-zinc-500 font-light leading-relaxed"
                        >
                            Responsable: Santiago Lorenzo Garrido. Finalidad: responder a
                            tu solicitud y, si lo aceptas, enviarte comunicaciones.
                            Derechos: acceso, rectificación, supresión y otros, en
                            santilorenzo@desactivaelpatron.com.
                        </p>

                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="exit-close-button"
                            className="block w-full text-center text-[11px] tracking-[0.2em] uppercase text-zinc-500 hover:text-zinc-300 transition-colors"
                        >
                            No, gracias — seguir esperando el momento perfecto
                        </button>
                    </form>
                </div>
            </DialogContent>
        </Dialog>
    );
}
